import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  activateSupplier,
  createSupplier,
  deleteSupplier,
  listSuppliers,
  purgeSupplier,
  updateSupplier,
  type SupplierListParams,
  type SupplierListResult,
} from './api';
import type { Supplier, SupplierCreateInput, SupplierUpdateInput } from './types';

const KEY = 'suppliers' as const;

export const useSuppliersList = (params: SupplierListParams = {}) => {
  return useQuery<SupplierListResult, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listSuppliers(params),
    placeholderData: (prev) => prev,
  });
};

export const useCreateSupplier = () => {
  const qc = useQueryClient();
  return useMutation<Supplier, ApiClientError, SupplierCreateInput>({
    mutationFn: (body) => createSupplier(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useUpdateSupplier = () => {
  const qc = useQueryClient();
  return useMutation<Supplier, ApiClientError, { id: string; body: SupplierUpdateInput }>({
    mutationFn: ({ id, body }) => updateSupplier(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeleteSupplier = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => deleteSupplier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePurgeSupplier = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeSupplier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateSupplier = () => {
  const qc = useQueryClient();
  return useMutation<Supplier, ApiClientError, string>({
    mutationFn: (id) => activateSupplier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
